import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { useClickAway, useKey } from 'react-use';
import styled, { css } from 'styled-components';

import Icon from 'shell/components/Icon';
import Label from 'shell/components/Label';
import Text from 'shell/components/Text';

const ALPHABET = [
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
];

const MultiSelect = ({
    id, // string
    disabled, // boolean
    error, // string
    label, // string
    options, // object[] of shape { key: string, value: string }
    placeholder = 'Select options...', // string
    required, // boolean
    selected, // object[] of shape { key: string, value: string }
    onChange, // function
    ...rest
}) => {
    if (label && !id) throw new Error('Shell: Missing id in component Select.');

    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const menuItemRefs = useRef(options.map(() => createRef()));

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [focusIndex, setFocusIndex] = useState(0);

    const openMenu = () => setIsMenuOpen(true);

    const closeMenu = () => {
        setIsMenuOpen(false);
        buttonRef.current.focus();
    };

    const toggleMenu = () => setIsMenuOpen((current) => !current);

    const selectItem = useCallback(
        (item) => {
            if (selected.some(({ key }) => key === item.key)) {
                onChange(selected.filter(({ key }) => key !== item.key));
            } else {
                onChange([...selected, item]);
            }
        },
        [selected]
    );

    const focusPrevIndex = () => {
        setFocusIndex((current) => {
            if (current === 0) return current;
            return current - 1;
        });
    };

    const focusNextIndex = () => {
        setFocusIndex((current) => {
            if (current === options.length - 1) return current;
            return current + 1;
        });
    };

    useClickAway(menuRef, (e) => {
        if (isMenuOpen) {
            e.preventDefault();
            closeMenu();
        }
    });

    useEffect(() => {
        if (isMenuOpen) {
            menuItemRefs.current[focusIndex].current.focus();
        }
    }, [isMenuOpen, focusIndex]);

    useKey(
        ' ',
        (e) => {
            if (buttonRef.current === document.activeElement) {
                openMenu();
            }

            if (menuRef.current.contains(document.activeElement)) {
                e.preventDefault();
                selectItem(options[focusIndex]);
            }
        },
        {},
        [focusIndex, selectItem]
    );

    useKey('ArrowUp', (e) => {
        if (buttonRef.current === document.activeElement) {
            e.preventDefault();
            openMenu();
        }

        if (menuRef.current.contains(document.activeElement)) {
            e.preventDefault();
            focusPrevIndex();
        }
    });

    useKey('ArrowDown', (e) => {
        if (buttonRef.current === document.activeElement) {
            e.preventDefault();
            openMenu();
        }

        if (menuRef.current.contains(document.activeElement)) {
            e.preventDefault();
            focusNextIndex();
        }
    });

    useKey(
        'Enter',
        () => {
            if (menuRef.current.contains(document.activeElement)) {
                selectItem(options[focusIndex]);
            }
        },
        {},
        [focusIndex]
    );

    useKey(
        'Escape',
        () => {
            if (isMenuOpen) {
                closeMenu();
            }
        },
        {},
        [isMenuOpen]
    );

    useKey(
        'Tab',
        (e) => {
            if (isMenuOpen) {
                e.preventDefault();
            }
        },
        {},
        [isMenuOpen]
    );

    useKey(
        (e) => {
            if (isMenuOpen) {
                const key = e.key.toLowerCase();
                if (ALPHABET.includes(key)) {
                    const firstMatch = options.findIndex(({ value }) => value.toLowerCase().startsWith(key));
                    if (firstMatch > -1) {
                        const nextMatch = options.findIndex(
                            ({ value }, index) => value.toLowerCase().startsWith(key) && index > focusIndex
                        );
                        setFocusIndex(nextMatch > -1 ? nextMatch : firstMatch);
                    }
                }
            }
        },
        {},
        [isMenuOpen, focusIndex]
    );

    return (
        <StyledDiv {...rest}>
            {label && (
                <StyledLabel id={id} disabled={disabled} onClick={() => buttonRef.current.focus()}>
                    {label} {required && '*'}
                </StyledLabel>
            )}
            <StyledButton
                id={id}
                ref={buttonRef}
                disabled={disabled}
                aria-expanded={isMenuOpen}
                aria-haspopup={'listbox'}
                onMouseDown={toggleMenu}
                onKeyPress={(e) => e.preventDefault()}
                onKeyUp={(e) => e.preventDefault()}
                {...(label ? { 'aria-labelledby': id } : null)}
            >
                <DefaultRenderWrapper>
                    {selected.map(({ value }) => value).join(', ') || (
                        <Text color={'neutralLight'} variant={'body2'}>
                            {placeholder}
                        </Text>
                    )}
                    <StyledIcon isShown={true} kind={isMenuOpen ? 'dropdownArrowUp' : 'dropdownArrowDown'} />
                </DefaultRenderWrapper>
            </StyledButton>
            {error && (
                <Text color={'error'} variant={'subtitle3'}>
                    • {error}
                </Text>
            )}
            <Menu
                ref={menuRef}
                role={'listbox'}
                buttonHeight={buttonRef.current?.clientHeight || 0}
                isOpen={isMenuOpen}
                hasLabel={Boolean(label)}
            >
                {options.map((option, index) => (
                    <MenuItem
                        ref={menuItemRefs.current[index]}
                        id={option.key}
                        key={option.key}
                        aria-selected={selected.some(({ key }) => key === option.key)}
                        role={'option'}
                        tabIndex={focusIndex === index ? 0 : -1}
                        isSelected={selected.some(({ key }) => key === option.key)}
                        onClick={() => selectItem(option)}
                        onMouseEnter={() => setFocusIndex(index)}
                    >
                        {option.value}
                    </MenuItem>
                ))}
            </Menu>
        </StyledDiv>
    );
};

const StyledDiv = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.spacing(2)};
    position: relative;
`;

const StyledLabel = styled(Label)`
    cursor: default;
`;

const StyledButton = styled.button`
    display: flex;
    align-items: center;
    border: 1px solid ${(props) => props.theme.palette.neutral['500']};
    border-radius: 6px;
    width: 100%;
    height: 36px;
    padding: ${(props) => props.theme.spacing(2, 4)};
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.1px;
    line-height: 20px;
    text-align: left;
    background-color: ${(props) => props.theme.palette.white};

    &:focus {
        border-color: ${(props) => props.theme.palette.primary['500']};
        outline: auto;
        outline-offset: 1px;

        & svg {
            opacity: 1;
        }
    }

    &:hover {
        border-color: ${(props) => props.theme.palette.primary['500']};

        & svg {
            opacity: 1;
        }
    }

    &:disabled {
        border-color: ${(props) => props.theme.palette.neutral['400']};
        color: ${(props) => props.theme.palette.neutral['400']};
    }

    & > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const DefaultRenderWrapper = styled.span`
    position: relative;
    min-width: 100%;
    height: 100%;
    padding-right: ${(props) => props.theme.spacing(4)};
`;

const StyledIcon = styled(({ isShown, ...rest }) => <Icon {...rest} />)`
    position: absolute;
    bottom: 50%;
    right: 0;
    opacity: 0;
    transform: translateY(50%);
    transition: all 150ms;

    ${(props) =>
        props.isShown &&
        css`
            opacity: 1;
        `}
`;

const Menu = styled.ul`
    position: absolute;
    z-index: 1;
    border-radius: 6px;
    margin: ${(props) => props.theme.spacing(1, 0, 0)};
    width: 100%;
    max-height: ${(props) => props.theme.spacing(62)};
    padding: ${(props) => props.theme.spacing(4)};
    overflow-y: auto;
    background-color: ${(props) => props.theme.palette.white};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1), 0 2px 2px rgba(0, 0, 0, 0.03);

    top: ${(props) => props.buttonHeight + (props.hasLabel ? 28 : 0)}px;

    ${(props) =>
        props.isCustom &&
        css`
            width: fit-content;
        `}

    ${(props) =>
        !props.isOpen &&
        css`
            display: none;
        `}
`;

const MenuItem = styled.li`
    display: flex;
    align-items: center;
    list-style-type: none;
    border-radius: 6px;
    height: fit-content;
    min-height: 24px;
    padding: ${(props) => props.theme.spacing(0.5, 2)};
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.1px;
    line-height: 20px;
    cursor: default;

    &:not(:last-child) {
        margin-bottom: ${(props) => props.theme.spacing(2)};
    }

    &:focus {
        background-color: ${(props) => props.theme.palette.primary['50']};
        outline: auto;
    }

    ${(props) =>
        props.isSelected &&
        css`
            background-color: ${(props) => props.theme.palette.primary['50']};
        `}
`;

export default MultiSelect;
